import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import * as Enums from "@/utils/enums";
import { IconTrash } from "@tabler/icons";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import { IconPlus } from "@tabler/icons-react";
import { Anchor, Box, Button, Flex, Menu, Text, useMantineTheme } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { SimpleColumnMapping } from "@/PageComponents/SimpleTable/SimpleTable";
import featureService from "@/services/feature/feature-service";
import constants from "@/utils/constants";
import helper from "@/utils/helper";
import SCModal from "@/PageComponents/Modal/SCModal";
import ImageWithZoom from "@/PageComponents/Attachment/ImageWithZoom";
import permissionService from "@/services/permission/permission-service";
import Helper from "@/utils/helper";
import { calculateLineTotal, calculateLineTotalInclusive, calculateInclusive, calculateExclusive, calculateExclusiveUnitPrice, calculateMarkup, calculatePriceFromMarkup, calculateLineProfit, roundPrice } from '@/utils/line-totals';
import WarehouseTypeIcon from "@/PageComponents/Warehouse/WarehouseTypeIcon";

const addMissingIDs = (items: any[]) => {
    return items.map((x: any, i: any) => x.ID ? x : { ...x, ID: crypto.randomUUID() || 'item' + i })
}

const historyName = 'invoices'

const InvoiceSectionTable: FC<
    {
        descriptionColumnWidth?: string
        invoice: any;
        invoiceItems: any[];
        itemId: string;
        module: number;
        onDataUpdate: (newData: any[]/*, newSections: any[]*/) => void;
        addOptions: any[];
        onAddAction: (name: string) => void;
        onAddToSectionAction: (name: string, group: any) => void
        integration: boolean;
        companyTaxPercentage: number;
        onItemClicked: (item: any, index: number) => void
        onPredictedDefaultSectionPdfSettingsChanged?: (pdfSettings: { HideLineItems: boolean; DisplaySubtotal: boolean; }) => void
        customChildren?: ReactNode
        onColumnMappingLoaded?: (mapping: SimpleColumnMapping[]) => void
        userColumnConfig?: any[]
    }> = (props: any) => {

        const theme = useMantineTheme();
        const { data: hasStockControl } = useQuery(['hasStockControl'], () => featureService.getFeature(constants.features.STOCK_CONTROL));

        const [showImage, setShowImage] = useState<any>();

        const canUpdate = useMemo(() => props.invoice.InvoiceStatus === Enums.InvoiceStatus.Draft, [props.invoice.InvoiceStatus])

        const [currentData, setCurrentData] = useState(
            addMissingIDs(props.invoiceItems)
        )

        const [currentSections, setCurrentSections] = useState<any[]>([])

        const [savingItem, setSavingItem] = useState(false)


        /*const handleUndo = () => {
            if (history.length > 1) {
                const [data, sections] = history[history.length - 2]
                /!*data && sections && console.log('updating data',
                    data, sections
                )*!/
                data && sections && handleDataUpdate(
                    data/!*.map(x => {
                        const RowVersion = currentData.find((y: any) => x.ID === y.ID)?.RowVersion
                        return {...x, RowVersion}
                    })*!/,
                    sections,
                    false
                )
                setHistory(p => p.slice(0, -1))
            }
        }*/

        /** debounce all data updates and only trigger onUpdate callback when data was changed internally */
        const [debounced, setDebounced] = useDebouncedState([currentData, currentSections], 0)
        const changed = useRef(false)
        useEffect(() => {
            setDebounced(currentData)
        }, [currentData/*, currentSections*/])
        useEffect(() => {
            if (changed.current) {
                props.onDataUpdate(debounced)
                changed.current = false
                setSavingItem(true)
            }
        }, [debounced]);
        /** update current data when data changes externally - update only row version if data change was triggered with table data update internally*/
        const cancelNextUpdate = useRef(false)
        useEffect(() => {
            if (props.invoiceItems) {
                if (!cancelNextUpdate.current) {
                    const itemsWithIDs = addMissingIDs(props.invoiceItems)

                    setCurrentData(itemsWithIDs)
                    /*props.invoiceItems.length !== currentData.length && // if length changed the data was modified form outside and history needs to be appended
                    setHistory((p) => ([...p, [
                        itemsWithIDs,
                        getSectionsFromTableData(itemsWithIDs, 'InventorySectionName', 'InventorySectionID', Enums.Module.JobCard, props.itemId)
                    ]]))*/
                } else {
                    cancelNextUpdate.current = false
                }
                setSavingItem(false)
            }
        }, [props.invoiceItems])

        useEffect(() => {
            if (mapping.length) {
                props.onColumnMappingLoaded && props.onColumnMappingLoaded(mapping)
            }
        }, []);

        const handleDataUpdate = (newData: any/*, newSections*//*, updateHistory = true*/) => {
            setCurrentData(newData)
            changed.current = true
            cancelNextUpdate.current = true
            // updateHistory && setHistory((p) => [...p, [newData, newSections]])
        }

        const handleInputChange = (key: any, item: any, newValue: any) => {
            if (key === 'Description' || key === 'InventoryDescription') {
                handleDataUpdate(
                    currentData.map((x: any) => x.ID === item.ID ? ({ ...x, Description: newValue, InventoryDescription: newValue }) : x),
                    // currentSections
                )
            } else if (key === 'Quantity' || key === 'TaxPercentage' || key === 'LineDiscountPercentage' || key === 'UnitPriceExclusive') {
                const newItem = {
                    ...item,
                    [key]: newValue
                }
                if (newItem.LineDiscountPercentage < 0 || newItem.LineDiscountPercentage > 100) {
                    newItem.LineDiscountPercentage = newItem.LineDiscountPercentage < 0 ? 0 : 100
                }
                if (newItem.Quantity > 0) {
                    newItem.LineTotalExclusive = calculateLineTotal({
                        quantity: newItem.Quantity,
                        unitPrice: newItem.UnitPriceExclusive,
                        discountPercentage: newItem.LineDiscountPercentage,
                    });
                } else {
                    newItem.LineTotalExclusive = 0;
                }

                handleDataUpdate(
                    currentData.map((x: any) => x.ID === item.ID ? ({ ...x, ...newItem }) : x),
                    // currentSections
                )
            } else if (key === 'UnitPriceInclusive') {
                // Extract tax percentage from the item
                const taxPercentage = +item.TaxPercentage || 0;

                // Create a new item with updated values
                const newItem = {
                    ...item,
                    UnitPriceExclusive: calculateExclusiveUnitPrice(newValue, taxPercentage)
                };

                // The INCLUSIVE price is the one that was typed, so the line total is
                // calculated on that side and the exclusive amount taken as the remainder.
                // Deriving it from the exclusive unit price instead loses a cent - at 15%
                // an entered R80.00 would come back as R80.01.
                if (newItem.Quantity > 0) {
                    const lineTotalInclusive = calculateLineTotal({
                        quantity: newItem.Quantity,
                        unitPrice: newValue,
                        discountPercentage: newItem.LineDiscountPercentage,
                    });
                    newItem.LineTotalExclusive = calculateExclusive(lineTotalInclusive, taxPercentage);
                } else {
                    newItem.LineTotalExclusive = 0;
                }

                handleDataUpdate(
                    currentData.map((x: any) => x.ID === item.ID ? ({ ...x, ...newItem }) : x)
                );
            } else {
                handleDataUpdate(
                    currentData.map((x: any) => x.ID === item.ID ? ({ ...x, [key]: newValue }) : x),
                    // currentSections
                )
            }
        }

        const handleItemAction = (actionName: string, item: any, itemIndex: number, group: any) => {
            if (actionName === 'code') {
                props.onItemClicked(item, itemIndex)
            } else if (actionName === 'delete') {
                handleDataUpdate(currentData.filter((x: any) => x.ID !== item.ID)/*, newSections*/)
            }
        }
        const handleSectionAction = (name: any, group: any) => {
            if (name === 'delete') {
                handleDataUpdate(
                    currentData.filter((x: any) => x.InventorySectionID !== group.id).map((x: any, i: any) => ({ ...x, LineNumber: i + 1 })),
                )
            } else {
                props.onAddToSectionAction(name, group)
            }
        }

        const [hasManageCostingPermission] = useState(permissionService.hasPermission(Enums.PermissionName.ManageCosting));

        const mapping = useMemo(() => {
            return [
                ...((currentData ?? []).filter((x: any) => x.Inventory?.ThumbnailUrl).length > 0 ? [{
                    key: 'ImageUrl',
                    label: '',
                    valueFunction: (item: any) => item.Inventory?.ThumbnailUrl && <img
                        src={item.Inventory?.ThumbnailUrl}
                        style={{ height: 36, width: 36, borderRadius: 36, cursor: "pointer" }}
                        onClick={() => setShowImage(item)}
                    />
                }] : []),
                {
                    key: 'InventoryCode',
                    label: 'Code',
                    valueFunction: (item: any) => item.ProductID ? item.ProductNumber : item.InventoryCode ?? (canUpdate ? 'Edit' : ''),
                    linkAction: 'code'
                },
                {
                    key: 'Description',
                    keyFunction: (item: any) => item.InventoryID ? 'Description' : 'Description',
                    label: 'Description',
                    // type: canUpdate ? 'textInput' : undefined,
                    typeFunction: (item: any) => item.InventoryID ? 'textInput' : 'textArea',
                    stylingProps: {
                        miw: props.descriptionColumnWidth ?? '30vw',
                        darkerText: true
                    }
                },
                {
                    key: 'InvoiceItemType',
                    label: 'Status',
                    type: 'status',
                    colorFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (item.Integrated ? 'Green' : item.IntegrationMessage ? `Yellow` : undefined) : undefined),
                    valueFunction: (item: any) => (
                        item.InvoiceItemType === Enums.InvoiceItemType.Inventory && (item.Integrated ? 'Synced' : item.IntegrationMessage ? 'Error' : 'Not Synced'
                        ) || null),
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                },
                {
                    key: 'Quantity',
                    label: 'Quantity',
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    min: 0,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory)
                },
                ...(hasStockControl && [{
                    key: 'Warehouse',
                    label: 'Warehouse',
                    alignRight: false,
                    valueFunction: (item: any) => {
                        return helper.isInventoryWarehoused(item.Inventory) ?
                            <Flex align={"center"} gap={'xs'}>
                                <WarehouseTypeIcon warehouse={item.Warehouse} />
                                <span style={item.Warehouse ? {} : { color: theme.colors.yellow[6], fontWeight: "bold" }} title={item.Warehouse?.Name}>{item.Warehouse?.Code ?? "Unallocated"}</span>
                            </Flex>
                            : <></>;
                    }
                }] || []),
                {
                    key: 'UnitPriceExclusive',
                    label: 'Price Excl',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    // min: 0,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: true,
                        disabled: true,
                    }
                },
                {
                    key: 'UnitPriceInclusive',
                    label: 'Price Incl',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    valueFunction: (item: any) => {
                        // Calculate UnitPriceInclusive from UnitPriceExclusive and TaxPercentage
                        return calculateInclusive(item.UnitPriceExclusive, item.TaxPercentage);
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: false,
                    }
                },
                {
                    key: 'LineDiscountPercentage',
                    label: 'Discount %',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    numberInputProps: {
                        min: 0,
                        max: 100
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'TaxPercentage',
                    label: 'Tax Rate',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'selectInput' : undefined) : undefined,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    selectOptions: [
                        {
                            label: 'Standard Rate',
                            value: props.companyTaxPercentage + ''
                        },
                        {
                            label: props.companyTaxPercentage === 0 ? 'Default (No VAT)' : 'No VAT',
                            value: 0 + ''
                        },
                    ].filter((x: any) => x.label !== 'Standard Rate' || props.companyTaxPercentage !== 0),
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'LineTotalExclusive',
                    label: 'Total Excl',
                    currencyValue: true,
                    alignRight: true,
                    // type: canUpdate ? 'numberInput' : undefined,
                    min: 0,
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: true,
                        disabled: true,
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                },
                {
                    key: 'LineTotalInclusive',
                    label: 'Total Incl',
                    currencyValue: true,
                    alignRight: true,
                    min: 0,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    valueFunction: (item: any) => {
                        if (!item || typeof item.LineTotalExclusive === 'undefined' || typeof item.TaxPercentage === 'undefined') {
                            return 0;
                        }

                    // Calculated from the unit price, not from the rounded exclusive line total -
                    // rebuilding it from that turns an entered R80.00 into R80.01.
                    return calculateLineTotalInclusive({
                        quantity: item.Quantity,
                        unitPrice: item.UnitPriceExclusive,
                        discountPercentage: item.LineDiscountPercentage,
                        taxPercentage: item.TaxPercentage,
                    });
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: false
                    }
                }
            ].filter((x: any) => x.key === 'InvoiceItemType' ? props.integration : true) as SimpleColumnMapping[]
        }, [currentData, canUpdate, hasManageCostingPermission, props.companyTaxPercentage, props.descriptionColumnWidth, props.integration])

        useEffect(() => {
            if (mapping.length) {
                props.onColumnMappingLoaded && props.onColumnMappingLoaded(mapping)
            }
        }, [mapping]);

        return <>
            <Box pos={'relative'}>
                <SectionTable
                    userColumnConfig={props.userColumnConfig}
                    customChildren={props.customChildren}
                    onPredictedDefaultSectionPdfSettingsChanged={props.onPredictedDefaultSectionPdfSettingsChanged}
                    showNewBundleHint
                    data={currentData}
                    savingItem={savingItem}
                    mapping={mapping}
                    controls={canUpdate ? [
                        {
                            name: 'delete',
                            // type: 'warning',
                            icon: <IconTrash />,
                            label: 'Remove',
                            activeLabel: 'Removing'
                        },
                    ] : []}
                    sectionControls={
                        canUpdate ?
                            [
                                ...props.addOptions.map((x: any) => ({
                                    name: x.link,
                                    icon: IconPlus,
                                    label: x.text
                                })).filter((x: any) => x.name !== 'AddBundle'),
                                {
                                    name: 'ungroup'
                                },
                                {
                                    name: 'delete',
                                    icon: IconTrash,
                                    color: 'yellow.7',
                                    label: 'Delete Section'
                                }
                            ] : []}
                    onSectionItem={handleSectionAction}
                    onDataUpdate={handleDataUpdate}
                    onAction={handleItemAction}
                    canEdit={canUpdate}
                    stylingProps={{
                        compact: true,
                        rows: false,
                        darkerText: true
                    }}
                    onInputChange={handleInputChange}
                    addButton={canUpdate ? {
                        // label: 'Add Material / Service',
                        // callback: props.onAddItem,
                        customComponent: <Flex w={'100%'} gap={'md'}>
                            <Menu
                                shadow="md"
                            >
                                <Menu.Target>
                                    <Button c={'scBlue'} size={'compact-sm'} variant={'transparent'} type={'button'} >
                                        <IconPlus size={16} />
                                        <Anchor size={'sm'}>
                                            Add Line
                                        </Anchor>
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {
                                        props.addOptions.map(
                                            (x: any, i: any) => (
                                                <Menu.Item key={x.text + i} onClick={() => props.onAddAction && props.onAddAction(x.link)}>
                                                    {x.text} {/*{x.newOption && <NewText h={12} />}*/}
                                                </Menu.Item>
                                            )
                                        )
                                    }
                                </Menu.Dropdown>
                            </Menu>
                        </Flex>
                    } : undefined}
                    height={'100%'}
                    sectionTitleKey={'InventorySectionName'}
                    sectionIdKey={'InventorySectionID'}
                    itemId={props.itemId}
                    module={Enums.Module.Quote}
                    useHideLineItemsPdfSetting
                    useDisplaySubtotalsPdfSetting
                />

            </Box>

            {showImage &&
                <SCModal
                    open={!!showImage}
                    showClose={true}
                    withCloseButton={true}
                    onClose={() => setShowImage(undefined)}
                    size={"auto"}>
                    <ImageWithZoom
                        attachment={{ ContentType: "image/jpeg", Url: showImage.Inventory?.ImageUrl ?? "", UrlThumb: showImage.Inventory?.ThumbnailUrl ?? "" }}
                    />
                </SCModal>
            }

        </>
    }
export default InvoiceSectionTable
